import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { toActivitiesUrl } from '@/utils/drillthrough';
import type { WBSHotspotRow } from '@/utils/analyticsCompute';

const TOP_N = 15;

export interface WBSHotspotsFloatTableProps {
  rows: WBSHotspotRow[];
  loading?: boolean;
}

export const WBSHotspotsFloatTable: React.FC<WBSHotspotsFloatTableProps> = ({
  rows: rawRows,
  loading = false,
}) => {
  const rows = React.useMemo(
    () => [...rawRows].sort((a, b) => b.hotspotScore - a.hotspotScore).slice(0, TOP_N),
    [rawRows]
  );

  if (loading) {
    return (
      <div className="p-3">
        <p className="text-xs text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-3">
        <p className="text-xs text-muted-foreground">No WBS data available.</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">WBS</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Hotspot</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Avg Float (d)</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Min Float (d)</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">% Crit/Near</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Count</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Overdue</th>
              <th className="w-16 py-1.5 px-2 text-muted-foreground font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.wbs_id} className="border-b border-border/40 hover:bg-muted/20">
                <td className="py-1.5 px-2 text-foreground truncate max-w-[160px]">
                  {row.wbs_short_name || row.wbs_name || row.wbs_id}
                </td>
                <td className="py-1.5 px-2 tabular-nums text-foreground font-medium text-right">{row.hotspotScore}</td>
                <td className="py-1.5 px-2 tabular-nums text-foreground text-right">{row.avgFloatDays}</td>
                <td
                  className={`py-1.5 px-2 tabular-nums text-right ${
                    row.minFloatDays < 0 ? 'text-destructive' : 'text-foreground'
                  }`}
                >
                  {row.minFloatDays}
                </td>
                <td className="py-1.5 px-2 tabular-nums text-muted-foreground text-right">{row.criticalNearCriticalPct}%</td>
                <td className="py-1.5 px-2 tabular-nums text-muted-foreground text-right">{row.total_activities}</td>
                <td className="py-1.5 px-2 tabular-nums text-muted-foreground text-right">{row.overdueCount}</td>
                <td className="py-1.5 px-2 text-right">
                  <Link
                    to={toActivitiesUrl({ wbs_id: row.wbs_id })}
                    className="inline-flex items-center justify-end gap-0.5 text-primary text-[11px] font-medium hover:underline"
                    title="View activities"
                  >
                    View
                    <ChevronRight className="h-3 w-3" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
